// The network probe: a deliberately tiny two-player game ("Tax Tap Duel") that exercises everything online play
// needs — matchmaking, room allocation, admission, commands in, views out, an outcome — on every backend
// (docs/NETTEST.md). It speaks the game's rpc packet format and msgpack framing, so what it measures is what the war
// will see. It is a transport fixture, not gameplay: its rules are plain TypeScript, not the ESEngine sim.

import { rpc } from '@esengine/rpc'
import type { Seat } from '../ids'

/** Client commands; `seq` strictly increases per player and seq <= the last one seen is ignored (as in the war). */
export type ProbeCommand = { readonly kind: 'tap' } | { readonly kind: 'forfeit' }

export interface ProbeCommandEnvelope {
  readonly seq: number
  readonly command: ProbeCommand
}

/** A latency probe, answered at once with a pong (not queued behind the tick). */
export interface ProbePing {
  readonly id: number
}

export interface ProbePong {
  readonly id: number
  /** The room tick when the ping arrived. */
  readonly tick: number
}

export interface ProbeRulesView {
  readonly tickHz: number
  readonly viewHz: number
  readonly targetTaps: number
  readonly matchSeconds: number
}

/** The room's first message to an admitted player; a client sends nothing before it. */
export interface ProbeWelcome {
  readonly room: string
  readonly seat: Seat
  readonly seats: number
  /** Who hosts the room, for the test report ("Cloudflare Durable Object", "PlayFab server EastUs"). */
  readonly host: string
  readonly rules: ProbeRulesView
  /** The last command seq the room has seen from this seat: a reconnecting client continues after it. */
  readonly lastSeq: number
}

export const PROBE_PHASES = ['waiting', 'countdown', 'running', 'ended'] as const
export type ProbePhase = (typeof PROBE_PHASES)[number]

export interface ProbeSeatView {
  readonly connected: boolean
  readonly taps: number
  /** The last command seq the room has seen from this seat (command-to-view latency). */
  readonly lastSeq: number
}

export interface ProbeView {
  readonly tick: number
  readonly phase: ProbePhase
  /** Ticks until the phase ends on its own (waiting: the no-show deadline; ended: 0). */
  readonly phaseTicksLeft: number
  readonly seats: readonly ProbeSeatView[]
}

export type WinReason = 'target' | 'time' | 'forfeit' | 'disconnect'
export type AbandonReason = 'no_show' | 'disconnected' | 'host_shutdown'

export type ProbeOutcome =
  | { readonly kind: 'won'; readonly winner: Seat; readonly by: WinReason; readonly taps: readonly number[] }
  | { readonly kind: 'draw'; readonly taps: readonly number[] }
  | { readonly kind: 'abandoned'; readonly reason: AbandonReason }

/**
 * Player privacy, rule 1 (ARCHITECTURE §15): room → client messages carry seats and game state only — never a
 * PlayerKey, an account id, an IP address or a location. A field added here goes to every player in the room.
 */
export const probeProtocol = rpc.define({
  api: {},
  msg: {
    /** client → room */
    command: rpc.msg<ProbeCommandEnvelope>(),
    ping: rpc.msg<ProbePing>(),
    /** room → client */
    welcome: rpc.msg<ProbeWelcome>(),
    view: rpc.msg<ProbeView>(),
    pong: rpc.msg<ProbePong>(),
    /** The outcome; the room closes the connection right after it. */
    ended: rpc.msg<ProbeOutcome>(),
  },
})

export type ProbeProtocol = typeof probeProtocol
